/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-var-requires */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MenuBar from '../../MenuBar/MenuBar';
import cytoscape from 'cytoscape';
import klay from 'cytoscape-klay';
import dagre from 'cytoscape-dagre';
import cola from 'cytoscape-cola';
import bilkent from 'cytoscape-cose-bilkent';
import euler from 'cytoscape-euler';
import fcose from 'cytoscape-fcose';
import cise from 'cytoscape-cise';
import expandCollapse from 'cytoscape-expand-collapse';
import cxtmenu from 'cytoscape-cxtmenu';
import { run as runActions } from '../../../store/workspaces/workspaces/run';
import tapNode from './events/tap.node';
import tabContext from './events/tab.context';
import tapEdge from './events/tab.edge';
import boxSelect from './events/boxSelect';
import { WidgetProvider } from '../WidgetContext';
import { useTranslation } from 'react-i18next';
import { mouseoverNode, mouseoverEdge, mouseoutNode, mouseoutEdge, mouseDown, mouseUp } from './events/mouseEvents';
import { dragPan } from './events/drag';
import { grabElement } from './events/grab';
import { dropElement } from './events/drop';
import { WidgetContainerStyled, WidgetContentStyled } from '../styles';
import { getStyles } from './services/getStyles';
import PropTypes from 'prop-types';
import 'cytoscape-context-menus/cytoscape-context-menus.css';
import './styles.css';
import { toast } from 'src/utils/notification';
import { removeNode } from './services/removeNodes';
import { applyKHop as applyKHopSvc, clearKHop as clearKHopSvc } from './services/khop';
import { toggleFullscreen as toggleFullscreenSvc } from './services/fullscreen';
import { getAllNodesAndEdgesSvc } from './services/getNodesAndEdges';
import { reloadLayoutSvc } from './services/reloadLayout';
import { zoomFitNodesSvc } from './services/zoomFit';
import { getState, makeGraphKey, saveState } from './graph.cache';
import GraphToolbar from './components/GraphToolbar';
import { contextMenu } from './Graph.edit.contextmenu';
import { addNodesCytoscape } from './services/addNodes';

const nodeHtmlLabel = require('cytoscape-node-html-label');
nodeHtmlLabel(cytoscape);

cytoscape.use(cise);
cytoscape.use(klay);
cytoscape.use(dagre);
cytoscape.use(cola);
cytoscape.use(bilkent);
cytoscape.use(euler);
cytoscape.use(fcose);
cytoscape.use(expandCollapse);
(cytoscape as any).use(cxtmenu);

const GraphWidgetEdit = ({ navData, actionsState }) => {
  const cyRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const fsRootRef = useRef<HTMLDivElement | null>(null); 
  const ecRef = useRef<any>(null);
  const [selectedElements, setSelectedElements] = useState([]);
  const { t } = useTranslation();
  const [cyInstance, setCyInstance] = useState<any>(null);
  const [kHop, setKHop] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layoutName, setLayoutName] = useState<any>('preset');
  const [graphData, setGraphData] = useState<any[] | null>(null);
  const didInitialFitRef = useRef(false);
  const [showToolbar, setShowToolbar] = useState(null);
  let contextMenuInstance: any;

  let cy: any;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const key = await makeGraphKey(navData, navData.widgetData);
        const cached = await getState(key); 

        if (!cancelled) {
          if (Array.isArray(cached) && cached.length > 0) {
            setGraphData(cached); 
          } else {
            setGraphData(null); 
          }
        }
      } catch (error: any) {
        if (!cancelled) toast.error('Cache konnte nicht gelesen werden.', error?.message || 'Unknown error');
      }
    };

    load();
    return () => { cancelled = true; setGraphData(null); };
  }, [navData.renderID, navData.widgetData?.dataID, navData.widgetData?.timestamp]);

  useEffect(() => {
    return () => {
      try {
        if (cyInstance) {
          const key = makeGraphKey(navData, navData.widgetData);
          saveState(key, cyInstance); 
        }
      } catch {}
    };
  }, [cyInstance, navData.renderID, navData.widgetData?.dataID]);

  useEffect(() => {
    contextMenuInstance?.destroy();
    renderCytoscapeElement(graphData, navData.widgetData.config);
    return () => {
      contextMenuInstance?.destroy();
      localStorage.setItem('selectedElements', JSON.stringify([]));
    };
  }, [navData.widgetData, graphData]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' || event.key === 'Del') removeNode(cy, {});
    };
    window.addEventListener('keydown', handleKeyDown);

    const fsHandler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', fsHandler);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', fsHandler);
    };
  }, []);

  useEffect(() => {
    if (!cyInstance) return;
    try {
      const prev = cyInstance.scratch('_layout') || {};
      cyInstance.scratch('_layout', { ...prev, name: layoutName });
    } catch {}
  }, [layoutName, cyInstance]);

  const getAllNodesAndEdges = useCallback(async () => { return getAllNodesAndEdgesSvc(cy); }, [cy]);
  const reloadLayout = useCallback(async (layout) => { reloadLayoutSvc(cy, layout); }, [cy]);
  const zoomFitNodes = useCallback(() => { zoomFitNodesSvc(cy); }, [cy]);
  const toggleFullscreen = async () => { await toggleFullscreenSvc(fsRootRef.current); };
  const applyKHop = (k: number) => { applyKHopSvc(cyInstance, k, setKHop, toast); };
  const clearKHop = () => { clearKHopSvc(cyInstance, setKHop); };

  const renderCytoscapeElement = async (elementsData: any[] | null, config: any) => {
    if (!elementsData || !Array.isArray(elementsData) || elementsData.length === 0) return;

    const style: cytoscape.StylesheetJson = await getStyles(config, navData.widgetData.style);
    const elements = JSON.parse(JSON.stringify(elementsData));

    cy = cytoscape({
      container: cyRef.current,
      style,
      elements,
      wheelSensitivity: 1,
      boxSelectionEnabled: true,
      layout: { name: 'preset', fit: false },
    });

    setCyInstance(cy);

    if (!didInitialFitRef.current) {
      requestAnimationFrame(() => {
        try {
          cy.resize();
          cy.fit(undefined, 24);
          didInitialFitRef.current = true;
        } catch {}
      });
    }

    try {
      (cy as any).expandCollapse && ((ecRef.current = (cy as any).expandCollapse({
        cueEnabled: false,
        animate: true,
        fisheye: false,
      })));
    } catch (e) {
      console.warn('expandCollapse init failed', e);
    }

    cy.style().selector('.hidden-by-khop').style({ display: 'none' }).update();

    cy.nodeHtmlLabel([{
      query: 'node.selected',
      halign: 'center',
      valign: 'bottom',
      halignBox: 'center',
      valignBox: 'bottom',
      cssClass: 'cy-infoblock',
      tpl(data: any) { return data?.infoblock == null ? '' : data.infoblock; },
    }]);

    {
      const ctx = navData.widgetData?.menu?.context ?? { elements: [] };
      contextMenuInstance = await contextMenu(
        cy,
        ctx,
        (nodes, position) => addNodesCytoscape(cy, nodes, position),
        (node) => {
          const nodeId = typeof node.id === 'function' ? node.id() : node.id;
          const resolvedId = typeof nodeId === 'function' ? nodeId() : nodeId;
          removeNode(cy, { id: resolvedId });
        },
        t
      );
    }

    if (navData.widgetData.events?.length > 0) {
      await tapNode(cy, navData.widgetData.events, runActions, actionsState);
      await tapEdge(cy, navData.widgetData.events, runActions, actionsState);
      await tabContext(cy, selectedElements);
      await mouseoverNode(cy);
      await mouseoverEdge(cy);
      await mouseoutNode(cy);
      await mouseoutEdge(cy);
      await boxSelect(cy, setSelectedElements);
      await mouseDown(cy, setSelectedElements);
      await dragPan(cy);
      await mouseUp(cy);
      await grabElement(cy);
      await dropElement(cy);
    }

    try {
      const prev = cy.scratch('_layout') || {};
      cy.scratch('_layout', { ...prev, name: 'preset', orientation: prev.orientation || 'LR' });
    } catch {}
  };

  if (!graphData || !navData.widgetData) return null;

  return (
    <WidgetProvider
      value={{
        getAllNodesAndEdges,
        widgetData: navData.widgetData,
        actionsState,
        reloadLayout,
        zoomFitNodes,
      }}
    >
      {navData.widgetData.menu && ( 
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0 basis-0">
            <MenuBar
              key={`menu-${navData.widgetData.menu.id}`}
              menu={navData.widgetData.menu}
              widgetTools={null}
            />
          </div>
          <div className="flex-none pb-2">
            <button
              type="button"
              onClick={() => { setShowToolbar(!showToolbar); }}
              className="flex-none w-9 h-9 inline-flex items-center justify-center rounded-full border border-gray-300 bg-white/80 hover:bg-white text-gray-600 shadow-sm backdrop-blur-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
            >
              <span className="e-icons e-settings text-gray-400" />
            </button>
          </div>
        </div>
      )}
      <div ref={fsRootRef} className="w-full h-full flex flex-col bg-white">
        <div className="flex-1 min-h-0">
          <WidgetContainerStyled>
            { showToolbar && <GraphToolbar
              cy={cyInstance}
              layoutName={layoutName}
              setLayoutName={setLayoutName}
              kHop={kHop}
              setKHop={setKHop}
              applyKHop={applyKHop}
              clearKHop={clearKHop}
              isFullscreen={isFullscreen}
              toggleFullscreen={toggleFullscreen}
              mode={''}
            />}
            <WidgetContentStyled>
              <div ref={wrapperRef} className="graph-container relative w-full h-full">
                <div className="w-full h-full">
                  <div className="node_selected w-full h-full">
                    {/* <div className="absolute top-2 right-2 z-20">
                      <span className="select-none rounded-full border border-blue-200 bg-blue-50 text-blue-300 text-[10px] font-medium px-2 py-0.5 tracking-wide uppercase">
                        edit
                      </span>
                    </div> */}
                    <div className="w-full h-full" style={{ backgroundColor: 'rgba(0, 128, 255, 0.03)' }} ref={cyRef} />
                  </div>
                </div>
                {/* Tooltip */}
              </div>
            </WidgetContentStyled>
          </WidgetContainerStyled>
        </div>
      </div>
    </WidgetProvider>
  );
};

GraphWidgetEdit.propTypes = {
  navData: PropTypes.object,
  actionsState: PropTypes.object,
  renderData: PropTypes.object,
  graphData: PropTypes.any,
};

export default GraphWidgetEdit;
